/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.32.0
 * @since 1.15.0
 */

import { Fragment, useCallback, useMemo } from 'react';

import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';

import { Button } from '../../../../components/common';
import { ImgStatusError, ImgStatusSuccess } from '../../../../components/common/Block/Img';
import FormFieldCheckbox from '../../../../modules/form/components/FormFieldCheckbox';
import FormFieldInput from '../../../../modules/form/components/FormFieldInput';
import FormFieldInputPassword from '../../../../modules/form/components/FormFieldInputPassword';
import {
    makeSelectPageOrderIdTranslation,
    makeSelectTranslationCommon,
} from '../../../../modules/internationalization/intSelectors';
import Modal from '../../../../modules/modal/components/Modal';
import { ModalCenterStyled } from '../../../../modules/modal/components/styled/ModalCenterStyled';
import { modalSetStatusAction } from '../../../../modules/modal/modalActions';
import { EModalStatus, IModalRenderProps } from '../../../../modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '../../../../modules/role/components/ButtonServiceUserDenied';
import { useSendOrderIdFileMutation } from '../../store';
import { EOrderIdFileModalField, IOrderIdFileModal } from '../../types';

export interface IOrderIdFileModalProps extends Pick<IModalRenderProps, 'onCloseModal'> {
    defaultEmail?: string;
    isCdmReport: boolean;
    orderId: string;
}

const OrderIdFileSendModal = ({
                                  defaultEmail,
                                  isCdmReport,
                                  onCloseModal,
                                  orderId,
                              }: IOrderIdFileModalProps) => {

    const dispatch = useDispatch();

    const [sendFile] = useSendOrderIdFileMutation();

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const translationOrderId = useSelector(makeSelectPageOrderIdTranslation());
    const isSendReportsNotificationsViaCncEnabled = useSelector(
        makeSelectFeatureEnabled(FeatureNameEnum.SEND_REPORTS_NOTIFICATIONS_VIA_CNC),
    );

    const initialValue: IOrderIdFileModal = {
        email: defaultEmail || '',
        zip: false,
        zipPasswordOptional: false,
        zipPassword: '',
    };

    const validationSchemaConf = useMemo(() => {
        return Yup.object().shape({
            [EOrderIdFileModalField.email]: Yup.string()
                .required(translationCommon.emailInput.hint)
                .email(translationCommon.emailInput.hint),
            [EOrderIdFileModalField.zip]: Yup.boolean(),
            [EOrderIdFileModalField.zipPasswordOptional]: Yup.boolean(),
            [EOrderIdFileModalField.zipPassword]: Yup.string().when(EOrderIdFileModalField.zipPasswordOptional, {
                is: (zipPasswordOptional: boolean) => zipPasswordOptional,
                then: Yup.string().required(translationCommon.passwordInput.hint),
            }),
        });
    }, [translationCommon.emailInput.hint, translationCommon.passwordInput.hint]);

    const onSubmit = useCallback((value: IOrderIdFileModal) => {
        sendFile({ ...value, orderId })
            .unwrap()
            .then(() => {
                dispatch(modalSetStatusAction({ status: EModalStatus.success }));
            })
            .catch(() => {
                dispatch(modalSetStatusAction({ status: EModalStatus.failure }));
            });
    }, [sendFile, orderId, dispatch]);

    return (
        <Modal
            dataName="order-id-file-modal"
            width="580px"
        >
            <Modal.Status status={EModalStatus.start}>
                <Modal.Title>
                    {translationOrderId.fileModal[EModalStatus.start].title}
                </Modal.Title>
                <Modal.Body>
                    <Formik
                        initialValues={initialValue}
                        validationSchema={validationSchemaConf}
                        onSubmit={onSubmit}
                    >
                        {
                            ({
                                 getFieldProps,
                                 isSubmitting,
                                 isValid,
                                 submitForm,
                             }) => (
                                <Fragment>
                                    <FormFieldInput
                                        dataName="order-id-field-email"
                                        label=""
                                        name={EOrderIdFileModalField.email}
                                    />
                                    {
                                        isCdmReport && !isSendReportsNotificationsViaCncEnabled && (
                                            <Fragment>
                                                <FormFieldCheckbox
                                                    dataName="order-id-field-zip"
                                                    label={translationOrderId.fileModal[EModalStatus.start]
                                                        .fields[EOrderIdFileModalField.zip]}
                                                    name={EOrderIdFileModalField.zip}
                                                />
                                                <FormFieldCheckbox
                                                    dataName="order-id-field-zip-password-optional"
                                                    disabled={!getFieldProps(EOrderIdFileModalField.zip).value}
                                                    label={translationOrderId.fileModal[EModalStatus.start]
                                                        .fields[EOrderIdFileModalField.zipPasswordOptional]}
                                                    name={EOrderIdFileModalField.zipPasswordOptional}
                                                />
                                                <FormFieldInputPassword
                                                    dataName="order-id-field-zip-password"
                                                    disabled={!getFieldProps(
                                                        EOrderIdFileModalField.zipPasswordOptional).value}
                                                    label=""
                                                    name={EOrderIdFileModalField.zipPassword}
                                                />
                                            </Fragment>
                                        )
                                    }

                                    <Modal.Control>
                                        <ButtonServiceUserDenied
                                            disabled={!isValid || isSubmitting}
                                            kind="medium"
                                            type="submit"

                                            onClick={submitForm}
                                        >
                                            {translationCommon.modal.send}
                                        </ButtonServiceUserDenied>
                                    </Modal.Control>
                                </Fragment>
                            )
                        }
                    </Formik>
                </Modal.Body>
            </Modal.Status>
            <Modal.Status status={EModalStatus.success}>
                <ModalCenterStyled>
                    <ImgStatusSuccess/>
                    <Modal.Title>
                        {translationOrderId.fileModal[EModalStatus.success].title}
                    </Modal.Title>
                    <Modal.Control>
                        <Button kind="medium" onClick={onCloseModal}>
                            {translationCommon.modal.good}
                        </Button>
                    </Modal.Control>
                </ModalCenterStyled>
            </Modal.Status>
            <Modal.Status status={EModalStatus.failure}>
                <ModalCenterStyled>
                    <ImgStatusError/>
                    <Modal.Title>{translationCommon.modal.error}</Modal.Title>
                    <Modal.Text>{translationCommon.modal.errorMsg.defaultMessage}</Modal.Text>
                    <Modal.Control>
                        <Button kind="medium" onClick={onCloseModal}>
                            {translationCommon.modal.close}
                        </Button>
                    </Modal.Control>
                </ModalCenterStyled>
            </Modal.Status>
        </Modal>
    );
};

export default OrderIdFileSendModal;
